// Farming unit editor — "Sensors" tab: the farming unit's own sensors (the
// Tal Gil controllers production edits in the farming unit modal's
// SensorsWrapper). The table lists them; selecting a row (or its map pin)
// opens its details beside the table, the header's "+" opens the Add new form
// (as in the season editor's sensors section).
//
// Fields and rules are production's EditSensor with `farmingUnitModal`
// (sensorEditRules.js with `isFarmingUnit`). Detail edits apply as the user
// types — the equivalent of EditSensor's "Done" — and land in the editor's
// farming unit draft, which "Save changes" sends like production's modal.
const React = require('react');
const { useState, useEffect, useRef, useCallback, useMemo } = require('react');
const { useTranslation } = require('react-i18next');
const GeneralClasses = require('GeneralClasses');
const locationManager = require('locationManager');
const rules = require('./sensorEditRules');
const { useSensorEditContext } = require('./SensorsSection');
const {
  SensorFields,
  SensorSettings,
  NewSensorFooter,
  SensorsCard,
} = require('./SensorEditor');

const coordinatesOf = (sensor) => {
  const lat = parseFloat(sensor?.coordinates?.latitude);
  const lng = parseFloat(sensor?.coordinates?.longitude);
  return isNaN(lat) || isNaN(lng) ? null : { lat, lng };
};

// The farming unit's sensors as map pins (the editor's map shows them while
// this tab is open).
const farmingUnitSensorMarkers = (sensors, selectedSerial, onSelect) =>
  Object.keys(sensors || {})
    .map((serial) => ({ serial, coordinates: coordinatesOf(sensors[serial]) }))
    .filter((item) => item.coordinates)
    .map(({ serial, coordinates }) => ({
      key: serial,
      lat: coordinates.lat,
      lng: coordinates.lng,
      title: sensors[serial].name || serial,
      selected: serial === selectedSerial,
      onClick: () => onSelect(serial),
    }));

const SensorsTable = ({ sensors, selectedSerial, onSelect }) => {
  const { t } = useTranslation();
  const serials = Object.keys(sensors || {});
  if (serials.length === 0) {
    return <div className='ns-empty-text'>{t('new_season_no_sensors')}</div>;
  }
  return (
    <table className='ns-table'>
      <thead>
        <tr>
          <th>{t('type')}</th>
          <th>{t('new_season_sensor_id')}</th>
        </tr>
      </thead>
      <tbody>
        {serials.map((serial) => (
          <tr
            key={serial}
            className={serial === selectedSerial ? 'ns-table-row-selected' : ''}
            onClick={() => onSelect(serial)}
          >
            <td>{rules.sensorTypeTitle(sensors[serial])}</td>
            <td>{sensors[serial].transmitter_id || serial}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// The selected sensor's draft; each draft change re-applies the draft to the
// sensor as it was when opened (production's editSensor on Done).
const useSelectedSensor = (sensors, serial, onChangeSensors, boundaries) => {
  const [draft, setDraft] = useState(null);
  const baseRef = useRef(null);
  const sensor = serial ? sensors?.[serial] : null;
  useEffect(() => {
    baseRef.current = sensor ? rules.cloneSensor(sensor) : null;
    setDraft(sensor ? rules.draftFromSensor(sensor, boundaries) : null);
    // Re-open only when another sensor is selected, not on every change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serial]);

  const changeSensor = useCallback(
    (change) =>
      onChangeSensors((current) => ({
        ...current,
        [serial]: change(current[serial]),
      })),
    [serial, onChangeSensors],
  );
  const onDraftChange = useCallback(
    (nextDraft) => {
      setDraft(nextDraft);
      changeSensor(() =>
        rules.applyDraft(rules.cloneSensor(baseRef.current), nextDraft),
      );
    },
    [changeSensor],
  );
  // A setting changes the sensor and the base the draft is applied to.
  const changeSetting = useCallback(
    (apply) => {
      baseRef.current = apply(rules.cloneSensor(baseRef.current));
      changeSensor((current) => apply(rules.cloneSensor(current)));
    },
    [changeSensor],
  );
  return { sensor, draft, onDraftChange, changeSetting };
};

// Sensor.jsx's row menu, for the selected sensor.
const settingsActionsFor = (serial, editing, onChangeSensors, deselect) => ({
  onToggleIgnore: () =>
    editing.changeSetting((sensor) =>
      rules.setIgnoreInCalculations(sensor, !sensor.ignore_in_calculations),
    ),
  onTogglePaused: () =>
    editing.changeSetting((sensor) => rules.setPaused(sensor, !sensor.paused)),
  onToggleWFR: () =>
    editing.changeSetting((sensor) =>
      rules.setConsiderAsWFR(sensor, !sensor.should_consider_as_wfr),
    ),
  onLocate: () =>
    locationManager
      .get_user_location()
      .then((position) =>
        editing.onDraftChange({
          ...editing.draft,
          ...rules.locatedCoordinates(position),
        }),
      )
      .catch((error) => console.log(error)),
  onDelete: () => {
    onChangeSensors((current) => {
      const { [serial]: removed, ...rest } = current;
      return rest;
    });
    deselect();
  },
});

// Save is blocked while "Add new" is open (production's modal keeps an open
// sensor edit out of the save) or the selected sensor's pressure boundaries
// are invalid.
const useBlockingReport = (newDraft, draft, onBlockingChange, t) => {
  const reason = newDraft
    ? t('edit_field_finish_open_edit_message')
    : draft && rules.isDraftInvalid(draft)
      ? t('max_bigger_than_min')
      : null;
  useEffect(() => {
    onBlockingChange(reason);
  }, [reason, onBlockingChange]);
  useEffect(() => () => onBlockingChange(null), [onBlockingChange]);
};

const canAddSensor = (draft) =>
  Boolean(draft.transmitterId) && !rules.isDraftInvalid(draft);

const useNewSensor = (sensors, onChangeSensors, boundaries) => {
  const [draft, setDraft] = useState(null);
  const open = () => setDraft(rules.newFarmingUnitSensorDraft(boundaries));
  const close = useCallback(() => setDraft(null), []);
  const onTypeChange = (type) =>
    setDraft(rules.emptyDraft({ type }, boundaries));
  const add = () => {
    const added = rules.newSensorFromDraft(draft, sensors, GeneralClasses);
    if (added) {
      onChangeSensors((current) => ({ ...current, [added.id]: added.sensor }));
    }
    close();
  };
  return { draft, setDraft, open, close, onTypeChange, add };
};

const FarmingUnitSensorsTab = ({
  sensors,
  selectedSerial,
  onSelect,
  onChangeSensors,
  onBlockingChange,
  onDetailsOpenChange,
  readOnly,
  farmingUnitName,
  distributionName,
}) => {
  const { t } = useTranslation();
  const context = useSensorEditContext(farmingUnitName, distributionName);
  const boundaries = context.defaultBoundaries;
  const editing = useSelectedSensor(
    sensors,
    selectedSerial,
    onChangeSensors,
    boundaries,
  );
  const newSensor = useNewSensor(sensors, onChangeSensors, boundaries);
  useBlockingReport(newSensor.draft, editing.draft, onBlockingChange, t);
  const isDetailsOpen = Boolean(newSensor.draft || editing.draft);
  useEffect(() => {
    onDetailsOpenChange(isDetailsOpen);
  }, [isDetailsOpen, onDetailsOpenChange]);
  useEffect(() => () => onDetailsOpenChange(false), [onDetailsOpenChange]);

  const closeNewSensor = newSensor.close;
  const selectRow = useCallback(
    (serial) => {
      closeNewSensor();
      onSelect(serial);
    },
    [closeNewSensor, onSelect],
  );
  const openNewSensor = () => {
    onSelect(null);
    newSensor.open();
  };
  const editorProps = useMemo(
    () => ({
      isFarmingUnit: true,
      fertilizerData: context.fertilizerData,
      lengthUnit: context.lengthUnit,
      showTensiometerRange: context.showTensiometerRange,
    }),
    [context],
  );

  return (
    <SensorsCard
      className='ns-fu-sensors-card'
      isOpen={Boolean(newSensor.draft || editing.draft)}
      addDisabled={readOnly || Boolean(newSensor.draft)}
      onAdd={openNewSensor}
      details={
        newSensor.draft ? (
          <div className='ns-sensor-details'>
            <SensorFields
              {...editorProps}
              draft={newSensor.draft}
              isNew
              onDraftChange={newSensor.setDraft}
              onTypeChange={newSensor.onTypeChange}
              valvesKey='new'
            />
            <NewSensorFooter
              seasonChoice={null}
              canAdd={canAddSensor(newSensor.draft)}
              onAdd={newSensor.add}
              onCancel={newSensor.close}
            />
          </div>
        ) : (
          editing.sensor &&
          editing.draft && (
            <fieldset className='ns-sensor-details' disabled={readOnly}>
              <SensorFields
                {...editorProps}
                draft={editing.draft}
                isNew={false}
                onDraftChange={editing.onDraftChange}
                valvesKey={selectedSerial}
              />
              {!readOnly && (
                <SensorSettings
                  sensor={editing.sensor}
                  actions={settingsActionsFor(
                    selectedSerial,
                    editing,
                    onChangeSensors,
                    () => onSelect(null),
                  )}
                />
              )}
            </fieldset>
          )
        )
      }
    >
      <SensorsTable
        sensors={sensors}
        selectedSerial={selectedSerial}
        onSelect={selectRow}
      />
    </SensorsCard>
  );
};

module.exports = FarmingUnitSensorsTab;
module.exports.farmingUnitSensorMarkers = farmingUnitSensorMarkers;
